import React, { useEffect, useReducer, useState } from 'react';
import { IconButton, Tabs, Tab, Button } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { formDataValidate } from '../../../../../utils/formUtils';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import { useDispatch, useSelector } from 'react-redux';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import {
  CreateActivityCategoryAPI,
  UpdateActivityCategoryAPI,
} from '../../../../../utils/activityCalendarAPIUtils';
import { allHotelsSelector } from '../../../../property/propertySlice';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { fetchAllActivityCategories } from '../../../cmsSlice';
import {
  ACTIVITY_CALENDAR_IMAGE_TYPE,
  TAB_LABEL_DETAILS,
  TAB_LABEL_MEDIA,
} from '../../../../../constants/ActivityCalendarConst';
import CustomCheckbox from '../../../../../shared/form-controls/custom-checkbox/Custom-Checkbox';

const ActivityCalendarCategoryCreate = (props) => {
  const { toggleDrawer, category, hotelId } = props;
  const originalHotels = useSelector(allHotelsSelector);
  const dispatch = useDispatch();
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [rawImages, setRawImages] = useState([]);
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Category Name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Category Code',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    isActive: {
      value: true,
      fieldName: 'Active',
      errors: {},
      validations: [],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  useEffect(() => {
    if (category) {
      const currFields = { ...formData };
      currFields.name.value = category?.name;
      currFields.code.value = category?.categoryCode;
      currFields.isActive.value = category?.isActive;
      currFields.isActive.value = category?.isActive;
      currFields.images.value = category?.images || [];
      setFormData(currFields);
    }
  }, [category]);

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const onNextClick = (e) => {
    e.preventDefault();
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      dispatch(stepUpLoaderCount());
      toggleDrawer('', false, '');
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      try {
        const images = await getProcessedImages(
          rawImages,
          originalHotels?.find((hotel) => hotel?.id === hotelId)?.groupId,
          ACTIVITY_CALENDAR_IMAGE_TYPE,
          hotelId,
        );
        const payload = {
          hotelId,
          name: formData?.name?.value,
          categoryCode: formData?.code?.value,
          isActive: formData?.isActive?.value,
          images,
        };
        if (category) {
          payload.id = category?.id;
          await UpdateActivityCategoryAPI(payload, category?.id, language);
          notificationManager('success', 'Activity category updated successfully');
        } else {
          await CreateActivityCategoryAPI(payload, language);
          notificationManager('success', 'Activity category created successfully');
        }
        dispatch(fetchAllActivityCategories(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          category
            ? notificationManager('danger', 'Failed to update category')
            : notificationManager('danger', 'Failed to create category');
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    } else {
      notificationManager('danger', 'Enter all required fields');
      if (!validate(1)) {
        setTabValue(1);
      }
      if (!validate(0)) {
        setTabValue(0);
      }
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else {
      if (validate(tabValue)) {
        setTabValue(newValue);
      }
    }
  };

  useEffect(() => {
    const currTabFromData = Object?.fromEntries(
      Object?.entries(formData)?.filter(([key, field]) => field?.tab === tabValue),
    );
    const isValid = formDataValidate(currTabFromData, false);
    setIsTabInValid(!isValid);
  }, [formData, tabValue]);

  const onImageListUpdated = (imgObjArr) => {
    setRawImages(imgObjArr);
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...formData?.images?.value];
    images?.splice(index, 1);
    setFormData({ images });
  };

  return (
    <form className='category-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column'>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-5'>
            <span className='page-title'>{category ? 'Edit Category' : 'Add Category'}</span>
          </div>
          <div className='col-md-6'>
            <Tabs
              value={tabValue}
              variant='fullWidth'
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className='property-tabs'
              aria-label='roomTypes-add-tabs'
            >
              <Tab label={TAB_LABEL_DETAILS} />
              <Tab label={TAB_LABEL_MEDIA} />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton aria-label='cancel' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>

        {tabValue === 0 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='row px-3 w-100 mt-4'>
              <div className='col-md-6'>
                <span className='page-title-secondary'>Details</span>
              </div>
            </div>
            <div className='row px-3 w-100 d-flex mt-3'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData?.name}
                  handleInputChange={(e) => {
                    setFormData({ name: e });
                  }}
                  maxLength={48}
                  endAdornment={true}
                  characterCount={true}
                  multiline={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData?.code}
                  handleInputChange={(e) => {
                    setFormData({ code: e });
                  }}
                  maxLength={15}
                  characterCount={true}
                  endAdornment={true}
                  multiline={true}
                />
              </div>
              <div className='col-md-2 mb-3'>
                <CustomCheckbox
                  item={formData?.isActive}
                  handleInputChange={(e) => {
                    setFormData({ isActive: e });
                  }}
                  color='primary'
                />
              </div>
            </div>
          </div>
        )}
        {tabValue === 1 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='col-md-12 mb-3'>
              <ImageUpload
                uploadedImages={formData?.images?.value}
                imgList={rawImages}
                onImageListUpdated={onImageListUpdated}
                onUploadedImageRemoval={onUploadedImageRemoval}
                onImagesLoading={(status) => {
                  setLoading(status);
                }}
                enabledMediaTabs={['image']}
              />
            </div>
          </div>
        )}

        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 1 ? (
              <Button
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
                disabled={isTabInValid}
                onClick={onNextClick}
              >
                Next
              </Button>
            ) : (
              <Button
                disabled={loading}
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
                onClick={handleSubmit}
              >
                {category ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};

export default ActivityCalendarCategoryCreate;
